/**
 * The Request Board's item panel — a fixed panel over the right edge of the
 * page (no backdrop) with full detail for one card: its stage (and the moves
 * any column it can move to), its rank, its images, and one activity
 * timeline that interleaves the item's history with its comment thread (where
 * the requester asks questions during review and the developer answers).
 *
 * Layout, top to bottom: a slim header (reference + type, "n of N" pager
 * through the lane, copy link, edit / delete, close); the title; a compact
 * label / value property list (Stage first); the description; images; the
 * activity timeline; and the composer pinned to the bottom.
 *
 * Images go on the item ("Add images", or drop / paste anywhere in the drawer
 * body) and on comments (the paperclip, or paste / drop into the comment box).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import InputBase from "@mui/material/InputBase";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import AddRounded from "@mui/icons-material/AddRounded";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import AttachFileRounded from "@mui/icons-material/AttachFileRounded";
import CheckRounded from "@mui/icons-material/CheckRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import DataObjectRounded from "@mui/icons-material/DataObjectRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import EditOutlined from "@mui/icons-material/EditOutlined";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import ImageOutlined from "@mui/icons-material/ImageOutlined";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRounded from "@mui/icons-material/KeyboardArrowUpRounded";
import LinkRounded from "@mui/icons-material/LinkRounded";
import StorageRounded from "@mui/icons-material/StorageRounded";
import SwapVertRounded from "@mui/icons-material/SwapVertRounded";
import CheckBoxOutlined from "@mui/icons-material/CheckBoxOutlined";
import QuestionAnswerOutlined from "@mui/icons-material/QuestionAnswerOutlined";
import ReplayRounded from "@mui/icons-material/ReplayRounded";
import Checkbox from "@mui/material/Checkbox";
import { useSnackbar } from "notistack";

import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";
import { partnerStorage } from "../../utils/storage/partnerStorage";
import JsonPreview from "../../components/common/JsonPreview";
import { slate, status } from "../../theme/colors";
import { segmentedGroupSx } from "../../theme/activeStates";
import { FormLinkTargetCard } from "../../components/forms/FormPrimitives";
import { A } from "../admin/shared/adminTokens";
import {
  COL,
  personFor,
  PLATFORMS,
  NEEDS,
  URGENCY,
  cleanNeeds,
  AUTO_PREFIX,
  displayTitle,
  isAuto,
  canMove,
  columnByKey,
  itemRef,
  tabsFor,
  moveLabel,
  movesFrom,
  canHit,
  hitSummary,
} from "./requestBoardModel";
import { EVENT } from "./requestBoardActivity";
import { BackendSpecSection, BlockerCallout } from "./requestBoardHandoff";
import {
  Avatar,
  ImageThumbs,
  Pill,
  NeedTags,
  AutoPill,
  CollapsibleSection,
  UrgencyPill,
  filesToImages,
  pastedImageFiles,
} from "./requestBoardParts";

/** Image files out of a paste or drop event; empty when there are none. */
const imagesFromEvent = (e) =>
  filesToImages(e.clipboardData ? pastedImageFiles(e) : e.dataTransfer?.files || []);

/** The panel's width: default, floor, and where it's remembered. */
const PANEL_DEFAULT_W = 640;
const PANEL_MIN_W = 480;
const PANEL_WIDTH_KEY = "requestBoard.panelWidth";

const MOD_KEY =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘" : "Ctrl";

/** How a person reads from the viewer's seat: the viewer is "You". */
const nameOf = (person, me) => {
  if (person && person === me) return "You";
  return personFor(person)?.name || person || "—";
};

/**
 * The panel's type scale and spacing, so every section reads the same:
 *   title 20/700 → body 14 → values 13.5 → labels 13 → meta 12.5 → section 11.5 caps
 * Sections sit SECTION apart (28px); rows inside a section ROW apart.
 */
const PT = {
  title: { fontSize: 20, fontWeight: 700, lineHeight: 1.3, letterSpacing: "-0.01em", color: A.ink },
  body: { fontSize: 14, lineHeight: 1.6, color: A.ink2 },
  // Everything that isn't the title / description is 12px.
  value: { fontSize: 12, lineHeight: 1.5, color: A.ink },
  label: { fontSize: 12, lineHeight: 1.5, color: A.muted },
  meta: { fontSize: 12, lineHeight: 1.5, color: A.faint },
  section: { fontSize: 11.5, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: A.muted },
};
const SECTION = 3.5;

/* ─────────────────────────────── pieces ─────────────────────────────── */

/**
 * Text that turns into an editor when clicked — the panel's request title and
 * its "More details". Saves on Enter (single line) / Ctrl+Enter (multiline) or
 * on blur; Esc puts the old text back. A required value can't be saved empty.
 */
const InlineEditText = ({
  value,
  onSave,
  label,
  multiline,
  required,
  placeholder,
  textSx,
  component = "div",
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  const commit = () => {
    const next = draft.trim();
    setEditing(false);
    if ((required && !next) || next === (value || "").trim()) {
      setDraft(value);
      return;
    }
    onSave(next);
  };
  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  // The same box in both states, so the text doesn't jump when it opens.
  const frame = {
    mx: "-8px",
    px: "8px",
    py: "4px",
    borderRadius: "8px",
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
  };

  if (editing) {
    return (
      <InputBase
        autoFocus
        fullWidth
        multiline
        maxRows={multiline ? 16 : 4}
        value={draft}
        placeholder={placeholder}
        inputProps={{ "aria-label": label }}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onFocus={(e) => {
          const el = e.target;
          el.setSelectionRange(el.value.length, el.value.length);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            cancel();
          } else if (e.key === "Enter" && (!multiline ? !e.shiftKey : e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            commit();
          }
        }}
        sx={{
          ...frame,
          display: "block",
          width: "calc(100% + 16px)",
          bgcolor: A.surface,
          boxShadow: `inset 0 0 0 1px ${A.teal}, 0 0 0 3px ${A.tealSoft}`,
          "& textarea": { ...textSx, p: 0 },
        }}
      />
    );
  }

  return (
    <Tooltip title={`Click to edit ${label.toLowerCase()}`} disableInteractive enterDelay={600}>
      <Box
        component={component}
        role="button"
        tabIndex={0}
        aria-label={`Edit ${label.toLowerCase()}`}
        onClick={() => setEditing(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setEditing(true);
          }
        }}
        sx={{
          m: 0,
          ...frame,
          ...textSx,
          cursor: "text",
          transition: "background-color .12s",
          "&:hover": { bgcolor: slate[50], boxShadow: `inset 0 0 0 1px ${A.line}` },
          "&:focus-visible": { outline: `2px solid ${A.tealSoft2}` },
          ...(!value && { color: A.faint }),
        }}
      >
        {value || placeholder}
      </Box>
    </Tooltip>
  );
};

InlineEditText.propTypes = {
  value: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  multiline: PropTypes.bool,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  textSx: PropTypes.object,
  component: PropTypes.string,
};

const Overline = ({ children, end }) => (
  <Flx ac jb g={1} sx={{ mb: 1.25, minHeight: 24 }}>
    <Txt sx={PT.section}>{children}</Txt>
    {end}
  </Flx>
);

Overline.propTypes = { children: PropTypes.node, end: PropTypes.node };

const Dot = ({ color, size = 8 }) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      width: size,
      height: size,
      borderRadius: "50%",
      bgcolor: color,
      flexShrink: 0,
    }}
  />
);

Dot.propTypes = { color: PropTypes.string, size: PropTypes.number };

/** A column name with its lane dot, inline in a sentence. */
const ColumnRef = ({ col }) => {
  const c = columnByKey(col);
  return (
    <Box component="span" sx={{ whiteSpace: "nowrap", fontWeight: 600, color: A.ink }}>
      <Dot color={c?.dot} size={7} />{" "}
      {c?.label || col}
    </Box>
  );
};

ColumnRef.propTypes = { col: PropTypes.string };

const HeaderIcon = ({ title, onClick, disabled, small, children }) => (
  <Tooltip title={title} disableInteractive>
    <span>
      <IconButton
        size="small"
        aria-label={title}
        onClick={onClick}
        disabled={disabled}
        sx={{
          color: A.muted,
          borderRadius: "7px",
          ...(small && { p: "3px" }),
          "& svg": { fontSize: small ? 18 : 20 },
        }}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

HeaderIcon.propTypes = {
  title: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  small: PropTypes.bool,
  children: PropTypes.node,
};

/** One label / value line of the compact property list. */
const PropRow = ({ label, children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "116px minmax(0, 1fr)",
      minHeight: 36,
      "& + &": { borderTop: `1px solid ${A.line}` },
    }}
  >
    <Txt
      component="div"
      sx={{ ...PT.label, px: 1.5, display: "flex", alignItems: "center", borderRight: `1px solid ${A.line}` }}
    >
      {label}
    </Txt>
    <Box sx={{ ...PT.value, minWidth: 0, px: 1.5, display: "flex", alignItems: "center" }}>
      {children}
    </Box>
  </Box>
);

PropRow.propTypes = { label: PropTypes.node, children: PropTypes.node };

/**
 * A value that opens a menu of options — the text plus a small chevron, no box,
 * so the property list keeps reading as text.
 */
const InlinePicker = ({ display, options, value, onPick, label }) => {
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <Box
        component="button"
        type="button"
        aria-label={`Change ${label}`}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          ml: "-6px",
          px: "6px",
          py: "3px",
          border: 0,
          borderRadius: "6px",
          bgcolor: "transparent",
          font: "inherit",
          color: "inherit",
          cursor: "pointer",
          "&:hover": { bgcolor: slate[100] },
          "&:focus-visible": { outline: `2px solid ${A.teal}`, outlineOffset: 1 },
        }}
      >
        {display}
        <ExpandMoreRounded sx={{ fontSize: 16, color: A.faint }} />
      </Box>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {options.map((o) => (
          <MenuItem
            key={o.value}
            selected={o.value === value}
            onClick={() => {
              setAnchor(null);
              onPick(o.value);
            }}
            sx={{ fontSize: 13.5, gap: 1 }}
          >
            {o.render || o.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

InlinePicker.propTypes = {
  display: PropTypes.node,
  options: PropTypes.array.isRequired,
  value: PropTypes.any,
  onPick: PropTypes.func.isRequired,
  label: PropTypes.string,
};

/**
 * The Stage row's value: the column the item sits in, as an inline picker
 * (same look as the other property pickers) whose options are the allowed moves.
 */
const ColumnSelect = ({ item, moves, onMove }) => {
  const [anchor, setAnchor] = useState(null);
  const col = columnByKey(item.column);
  const canChange = moves.length > 0;
  const button = (
    <Box
      component="button"
      type="button"
      aria-label="Change stage"
      disabled={!canChange}
      onClick={(e) => setAnchor(e.currentTarget)}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        ml: "-6px",
        px: "6px",
        py: "3px",
        border: 0,
        borderRadius: "6px",
        bgcolor: "transparent",
        font: "inherit",
        color: A.ink,
        cursor: canChange ? "pointer" : "default",
        textAlign: "left",
        "&:hover": canChange ? { bgcolor: slate[100] } : {},
        "&:focus-visible": { outline: `2px solid ${A.teal}`, outlineOffset: 1 },
      }}
    >
      <Dot color={col?.dot} size={8} />
      <span>{col?.label}</span>
      {canChange ? <ExpandMoreRounded sx={{ fontSize: 16, color: A.faint }} /> : null}
    </Box>
  );
  return (
    <>
      {canChange ? (
        button
      ) : (
        <Tooltip
          title={
            item.column === COL.DONE
              ? "Approved and closed."
              : "Nothing for you to move here; the next step is someone else's."
          }
        >
          <span>{button}</span>
        </Tooltip>
      )}
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 260 } } }}
      >
        <MenuItem disabled sx={{ fontSize: 13.5, gap: 1.25, opacity: "1 !important" }}>
          <Dot color={col?.dot} size={9} />
          <Box component="span" sx={{ flex: 1, fontWeight: 600 }}>
            {col?.label}
          </Box>
          <CheckRounded sx={{ fontSize: 17, color: A.tealDeep }} />
        </MenuItem>
        {moves.map((to) => {
          const target = columnByKey(to);
          const verb = moveLabel(item.column, to);
          return (
            <MenuItem
              key={to}
              onClick={() => {
                setAnchor(null);
                onMove(item.id, to);
              }}
              sx={{ fontSize: 13.5, gap: 1.25 }}
            >
              <Dot color={target?.dot} size={9} />
              <Box component="span" sx={{ flex: 1 }}>
                {target?.label}
              </Box>
              {verb.startsWith("Move to") ? null : (
                <Txt sx={{ fontSize: 12, color: A.muted }}>{verb}</Txt>
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

ColumnSelect.propTypes = {
  item: PropTypes.object.isRequired,
  moves: PropTypes.array.isRequired,
  onMove: PropTypes.func.isRequired,
};

const EVENT_ICON = {
  [EVENT.CREATED]: AddRounded,
  [EVENT.MOVED]: ArrowForwardRounded,
  [EVENT.REORDERED]: SwapVertRounded,
  [EVENT.IMAGES]: ImageOutlined,
  [EVENT.EDITED]: EditOutlined,
  [EVENT.HANDOFF]: StorageRounded,
  [EVENT.CHECK]: CheckBoxOutlined,
  [EVENT.ANSWERED]: QuestionAnswerOutlined,
  [EVENT.HIT]: ReplayRounded,
};

/** The sentence after the actor's name for one history event. */
const eventSentence = (e) => {
  switch (e.kind) {
    case EVENT.CREATED:
      return (
        <>
          filed this in <ColumnRef col={e.to} />
        </>
      );
    case EVENT.MOVED:
      return (
        <>
          moved this from <ColumnRef col={e.from} /> to <ColumnRef col={e.to} />
        </>
      );
    case EVENT.REORDERED:
      return (
        <>
          reordered it in <ColumnRef col={e.to} />
        </>
      );
    case EVENT.HANDOFF:
      return e.edit
        ? `updated the backend details: "${e.text}"`
        : `moved this to Requires Backend Changes: "${e.text}"`;
    case EVENT.ANSWERED:
      return `answered: "${e.text}"`;
    case EVENT.HIT:
      return "logged a recurring encounter";
    case EVENT.CHECK:
      return `${e.done ? "completed" : "reopened"} "${e.text}"`;
    default:
      return e.text ? `${e.text[0].toLowerCase()}${e.text.slice(1)}` : "";
  }
};

/** The Recurring Encounters description: how often you, and everyone, have run into it. */
const encounterDescription = (s, open) => {
  const times = (n) => `${n} time${n === 1 ? "" : "s"}`;
  if (!s.count) {
    return open
      ? "Nobody has run into this again yet. Log an encounter each time it gets in your way, so we can see what to fix first."
      : "Nobody logged an encounter before it was approved.";
  }
  const people = s.people.length;
  const total = `Run into ${times(s.count)}${people > 1 ? ` by ${people} people` : ""}`;
  const you = s.mine ? `you've run into it ${times(s.mine)}` : null;
  const last = s.lastAt ? `last on ${formatDateTime(s.lastAt, "datetime")}` : null;
  const line = [total, you, last].filter(Boolean).join(" · ");
  return open ? `${line}. Log another each time it comes up.` : `${line}. Approved, so it's no longer counted.`;
};

/**
 * Recurring Encounters — its own panel section, above Activity: how often
 * people have run into this and who keeps running into it, with the Log
 * encounter button on the header's right, shown even while the section is
 * folded. One click is one encounter; repeat clicks are the point. An Approved
 * card only shows its total (and no button).
 */
const EncountersSection = ({ item, me, onHit }) => {
  const s = hitSummary(item, me);
  const open = canHit(item);
  if (!open && !s.count) return null;
  return (
    <CollapsibleSection
      title="Recurring Encounters"
      meta={s.count ? String(s.count) : null}
      endAlways
      end={
        open ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddRounded />}
            onClick={() => onHit(item.id)}
            sx={{ height: 26, borderRadius: "7px", fontWeight: 600, fontSize: 12, whiteSpace: "nowrap" }}
          >
            Log encounter
          </Button>
        ) : null
      }
    >
      <Flx ac g={2} wrap>
        <Txt component="div" sx={{ ...PT.value, color: A.ink2, flex: 1, minWidth: 220 }}>
          {encounterDescription(s, open)}
        </Txt>
        {s.people.length ? (
          <Flx ac g={1} wrap>
            {s.people.slice(0, 6).map((p) => (
              <Tooltip
                key={p.by}
                disableInteractive
                title={`${nameOf(p.by, me)}: ${p.count} time${p.count === 1 ? "" : "s"}${p.lastAt ? ` · last ${formatDateTime(p.lastAt, "datetime")}` : ""}`}
              >
                <Flx ac g={0.5}>
                  <Avatar person={p.by} size={20} />
                  <Txt sx={{ ...PT.value, color: A.muted, fontWeight: 600 }}>{p.count}</Txt>
                </Flx>
              </Tooltip>
            ))}
          </Flx>
        ) : null}
      </Flx>
    </CollapsibleSection>
  );
};

EncountersSection.propTypes = {
  item: PropTypes.object.isRequired,
  me: PropTypes.string,
  onHit: PropTypes.func.isRequired,
};

/** History event row: an outlined icon disc on the rail, then one line. */
const EventRow = ({ event, me }) => {
  const Icon = EVENT_ICON[event.kind] || EditOutlined;
  return (
    <Flx g={1.5} sx={{ alignItems: "flex-start" }}>
      <Flx
        center
        sx={{
          width: 28,
          height: 28,
          flexShrink: 0,
          borderRadius: "50%",
          border: `1px solid ${A.line}`,
          bgcolor: A.surface,
          color: A.muted,
          position: "relative",
          zIndex: 1,
        }}
      >
        <Icon sx={{ fontSize: 15 }} />
      </Flx>
      <Txt
        component="div"
        sx={{ pt: "5px", fontSize: 12, lineHeight: 1.55, color: A.ink2, minWidth: 0 }}
      >
        <Box component="span" sx={{ fontWeight: 600, color: A.ink }}>
          {nameOf(event.actor, me)}
        </Box>{" "}
        {eventSentence(event)}{" "}
        <Box component="span" sx={{ ...PT.meta, fontSize: 11.5, whiteSpace: "nowrap" }}>
          {formatDateTime(event.at, "datetime")}
        </Box>
      </Txt>
    </Flx>
  );
};

EventRow.propTypes = { event: PropTypes.object.isRequired, me: PropTypes.string };

/** Comment row: avatar on the rail, name + time, then the body in a bubble. */
const CommentRow = ({ comment, me, onOpenImages }) => (
  <Flx g={1.5} sx={{ alignItems: "flex-start" }}>
    <Box sx={{ position: "relative", zIndex: 1, flexShrink: 0 }}>
      <Avatar person={comment.author} size={28} />
    </Box>
    <Flx column g={0.75} sx={{ minWidth: 0, flex: 1 }}>
      <Flx ac g={1} sx={{ pt: "4px" }}>
        <Txt sx={{ fontSize: 12, fontWeight: 600, color: A.ink }}>
          {nameOf(comment.author, me)}
        </Txt>
        <Txt sx={{ ...PT.meta, fontSize: 11.5 }}>
          {formatDateTime(comment.createdDateTime, "datetime")}
        </Txt>
      </Flx>
      {comment.body ? (
        <Box
          sx={{
            px: 1.75,
            py: 1.25,
            borderRadius: "10px",
            border: `1px solid ${A.line}`,
            bgcolor: slate[50],
            fontSize: 12,
            lineHeight: 1.55,
            color: A.ink2,
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
          }}
        >
          {comment.body}
        </Box>
      ) : null}
      <ImageThumbs images={comment.images} onOpen={onOpenImages} size={64} />
    </Flx>
  </Flx>
);

CommentRow.propTypes = {
  comment: PropTypes.object.isRequired,
  me: PropTypes.string,
  onOpenImages: PropTypes.func.isRequired,
};

const Kbd = ({ children }) => (
  <Box
    component="kbd"
    sx={{
      px: "6px",
      py: "1px",
      border: `1px solid ${A.line}`,
      borderBottomWidth: 2,
      borderRadius: "5px",
      bgcolor: slate[50],
      fontFamily: "inherit",
      fontSize: 11.5,
      fontWeight: 600,
      color: A.ink2,
    }}
  >
    {children}
  </Box>
);

Kbd.propTypes = { children: PropTypes.node };

/**
 * The Needs row: the card's tags, opening a menu of Fix / Change / Question
 * checkboxes. Each tick saves straight away; the menu stays open.
 */
const NeedsPicker = ({ value, onChange }) => {
  const [anchor, setAnchor] = useState(null);
  const toggle = (key) =>
    onChange(cleanNeeds(value.includes(key) ? value.filter((k) => k !== key) : [...value, key]));
  return (
    <>
      <Box
        component="button"
        type="button"
        aria-label="Change what this needs"
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          ml: "-6px",
          px: "6px",
          py: "4px",
          border: 0,
          borderRadius: "6px",
          bgcolor: "transparent",
          font: "inherit",
          color: "inherit",
          cursor: "pointer",
          "&:hover": { bgcolor: slate[100] },
          "&:focus-visible": { outline: `2px solid ${A.teal}`, outlineOffset: 1 },
        }}
      >
        {value.length ? (
          <Flx ac g={1.25} wrap>
            {value.map((k) => {
              const n = NEEDS.find((x) => x.key === k);
              return n ? (
                <Flx key={k} ac g={0.75}>
                  <Dot color={n.fg} size={8} />
                  <span>{n.label}</span>
                </Flx>
              ) : null;
            })}
          </Flx>
        ) : (
          <Box component="span" sx={{ color: A.faint }}>
            Not tagged yet
          </Box>
        )}
        <ExpandMoreRounded sx={{ fontSize: 16, color: A.faint }} />
      </Box>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {NEEDS.map((n) => (
          <MenuItem key={n.key} onClick={() => toggle(n.key)} sx={{ fontSize: 13.5, py: 0.25 }}>
            <Checkbox size="small" checked={value.includes(n.key)} sx={{ p: "6px", mr: 0.5 }} />
            {n.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

NeedsPicker.propTypes = { value: PropTypes.array.isRequired, onChange: PropTypes.func.isRequired };

/**
 * A question resolved without code. Answered: the answer in a green box
 * (click to edit). Tagged Question and not yet in review: an "Answer this
 * question" button that opens a box whose action saves the answer AND moves
 * the card to Completed - Awaiting Review.
 */
const AnswerBlock = ({ item, onAnswer, onUpdate }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setOpen(false);
    setDraft("");
  }, [item.id]);

  if (item.answer) {
    return (
      <Box
        sx={{
          mt: 1.5,
          px: 1.75,
          py: 1.25,
          borderRadius: "10px",
          bgcolor: status.success.bg,
          border: `1px solid ${status.success.border || A.line}`,
        }}
      >
        <Flx ac jb g={1} sx={{ mb: 0.5 }}>
          <Flx ac g={0.75} sx={{ color: status.success.fg, fontWeight: 700, fontSize: 13 }}>
            <QuestionAnswerOutlined sx={{ fontSize: 17 }} />
            Answer
          </Flx>
          {item.answeredBy ? (
            <Txt sx={{ fontSize: 12, color: A.muted }}>
              {personFor(item.answeredBy)?.name || item.answeredBy}
              {item.answeredAt ? ` · ${formatDateTime(item.answeredAt, "datetime")}` : ""}
            </Txt>
          ) : null}
        </Flx>
        <InlineEditText
          key={`answer-${item.id}`}
          label="Answer"
          value={item.answer}
          multiline
          required
          onSave={(v) => onUpdate(item.id, "answer", v, "answer")}
          textSx={{ fontSize: 13.5, lineHeight: 1.55, color: A.ink }}
        />
      </Box>
    );
  }

  const answerable =
    (item.needs || []).includes("question") && ![COL.REVIEW, COL.DONE].includes(item.column);
  if (!answerable) return null;

  if (!open) {
    return (
      <Box sx={{ mt: 1.5 }}>
        <Button
          size="small"
          variant="outlined"
          color="gray"
          startIcon={<QuestionAnswerOutlined />}
          onClick={() => setOpen(true)}
        >
          Answer this question
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mt: 1.5,
        border: `1px solid ${A.lineStrong}`,
        borderRadius: "10px",
        "&:focus-within": { borderColor: A.teal, boxShadow: `0 0 0 3px ${A.tealSoft}` },
      }}
    >
      <InputBase
        autoFocus
        fullWidth
        multiline
        minRows={3}
        maxRows={10}
        placeholder="Write the answer — it goes to the requester for review."
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        sx={{ px: 1.75, pt: 1.25, fontSize: 14, color: A.ink }}
      />
      <Flx ac jb g={1} sx={{ px: 1, pb: 1, pt: 0.5 }}>
        <Txt sx={{ fontSize: 12, color: A.faint, pl: 0.75 }}>
          Moves the card to Completed - Awaiting Review
        </Txt>
        <Flx ac g={0.75}>
          <Button size="small" variant="text" color="gray" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={!draft.trim() || saving}
            onClick={async () => {
              setSaving(true);
              const ok = await onAnswer(item.id, draft.trim());
              setSaving(false);
              if (ok) setOpen(false);
            }}
          >
            Answer and send to review
          </Button>
        </Flx>
      </Flx>
    </Box>
  );
};

AnswerBlock.propTypes = {
  item: PropTypes.object.isRequired,
  onAnswer: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
};

/** The card's raw JSON (as the API returned it), read-only with a copy button. */
const JsonDialog = ({ open, item, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  // `id` is the board's own copy of `_id`; show the API's shape.
  const raw = useMemo(() => {
    if (!item) return null;
    const { id, ...rest } = item;
    return rest;
  }, [item]);
  const json = useMemo(() => (raw ? JSON.stringify(raw, null, 2) : ""), [raw]);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: "14px", height: "80vh" } }}
    >
      <Flx column sx={{ height: "100%" }}>
        <Flx ac jb g={2} sx={{ px: 3, py: 2, borderBottom: `1px solid ${A.line}`, flexShrink: 0 }}>
          <Flx column g={0.25}>
            <Txt sx={{ fontSize: 17, fontWeight: 700, color: A.ink }}>{itemRef(item)} · JSON</Txt>
            <Txt sx={{ fontSize: 12.5, color: A.muted }}>The card as the API returns it.</Txt>
          </Flx>
          <Flx ac g={0.5}>
            <Button
              size="small"
              variant="outlined"
              color="gray"
              startIcon={<ContentCopyRounded />}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(json);
                  enqueueSnackbar("JSON copied", { variant: "success" });
                } catch {
                  enqueueSnackbar("Couldn't copy the JSON", { variant: "error" });
                }
              }}
            >
              Copy
            </Button>
            <IconButton size="small" aria-label="Close" onClick={onClose}>
              <CloseRounded sx={{ fontSize: 20 }} />
            </IconButton>
          </Flx>
        </Flx>
        {/* The app's standard JSON viewer: search, collapse levels, copy per value. */}
        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 3, py: 2 }}>
          {raw ? <JsonPreview values={raw} defaultCollapse={3} /> : null}
        </Box>
      </Flx>
    </Dialog>
  );
};

JsonDialog.propTypes = {
  open: PropTypes.bool,
  item: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

/* ─────────────────────────────── drawer ─────────────────────────────── */

export default function RequestBoardItemDrawer({
  item,
  me,
  lane = [],
  onClose,
  onNavigate,
  onMove,
  onReorder,
  onUpdate,
  onEditHandoff,
  onToggleTask,
  onAnswer,
  onComment,
  onAddImages,
  onRemoveImage,
  onOpenImages,
  onDelete,
  onEdit,
  onHit,
  history = [],
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [draft, setDraft] = useState("");
  const [draftImages, setDraftImages] = useState([]);
  const [dropOver, setDropOver] = useState(false);
  const [activityView, setActivityView] = useState("all");
  const itemPicker = useRef(null);
  const commentPicker = useRef(null);
  const composerRef = useRef(null);
  const specRef = useRef(null);
  const [jsonOpen, setJsonOpen] = useState(false);

  // Panel width: draggable from the left edge, remembered per browser.
  const [width, setWidth] = useState(() => {
    try {
      const v = Number(partnerStorage.getItem(PANEL_WIDTH_KEY));
      return v >= PANEL_MIN_W ? v : PANEL_DEFAULT_W;
    } catch {
      return PANEL_DEFAULT_W;
    }
  });
  const [resizing, setResizing] = useState(false);
  const clampWidth = (w) =>
    Math.round(Math.min(Math.max(w, PANEL_MIN_W), Math.max(PANEL_MIN_W, window.innerWidth - 240)));
  const saveWidth = (w) => {
    const next = clampWidth(w);
    setWidth(next);
    try {
      partnerStorage.setItem(PANEL_WIDTH_KEY, String(next));
    } catch {
      // storage unavailable — keep the width for this session only
    }
  };
  const startResize = (e) => {
    e.preventDefault();
    setResizing(true);
    const onMove = (ev) => setWidth(clampWidth(window.innerWidth - ev.clientX));
    const onUp = (ev) => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      setResizing(false);
      saveWidth(window.innerWidth - ev.clientX);
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // Ctrl/Cmd+V anywhere on the page attaches pasted images to the open item.
  // A paste inside the comment box is the comment's (handled there), and while
  // a dialog is open the paste belongs to that dialog's form.
  useEffect(() => {
    if (!item) return undefined;
    const onPaste = (e) => {
      if (composerRef.current?.contains(e.target)) return;
      if (document.querySelector(".MuiDialog-root")) return;
      const imgs = imagesFromEvent(e);
      if (!imgs.length) return;
      e.preventDefault();
      onAddImages(item.id, imgs);
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [item, onAddImages]);

  useEffect(() => {
    setDraft("");
    setDraftImages([]);
  }, [item?.id]);

  // History (minus comment events — the comments render themselves) and the
  // comments, oldest first, as one timeline.
  const timeline = useMemo(() => {
    if (!item) return [];
    const events = history
      .filter((e) => e.kind !== EVENT.COMMENTED)
      .map((e) => ({ key: e.id, at: e.at, event: e }));
    const comments = item.comments.map((c) => ({
      key: `c-${c.id}`,
      at: c.createdDateTime,
      comment: c,
    }));
    return [...events, ...comments].sort((a, b) => new Date(a.at) - new Date(b.at));
  }, [history, item]);

  if (!item) return null;

  const moves = movesFrom(item.column);
  const deletable = true;
  const canPost = !!draft.trim() || draftImages.length > 0;
  const idx = lane.findIndex((i) => i.id === item.id);
  const pos = idx + 1;
  const canReorder = canMove();
  // In Requests the rank (and the arrows) work within the card's section.
  const inRequests = item.column === COL.REQUESTS;
  const isUrgent = item.urgency === "urgent";
  const section = inRequests ? lane.filter((i) => (i.urgency === "urgent") === isUrgent) : lane;
  const sIdx = section.findIndex((i) => i.id === item.id);
  const shown =
    activityView === "comments" ? timeline.filter((t) => t.comment) : timeline;

  const post = () => {
    if (!canPost) return;
    onComment(item.id, draft.trim(), draftImages);
    setDraft("");
    setDraftImages([]);
  };

  const copyLink = async () => {
    const url = `${window.location.origin}${window.location.pathname}?item=${encodeURIComponent(item.id)}`;
    try {
      await navigator.clipboard.writeText(url);
      enqueueSnackbar(`Link to ${itemRef(item)} copied`, { variant: "success" });
    } catch {
      enqueueSnackbar("Couldn't copy the link", { variant: "error" });
    }
  };

  // Files dropped on the body attach to the ITEM; the comment box takes its own.
  const bodyDrop = {
    onDragOver: (e) => {
      if (![...(e.dataTransfer?.types || [])].includes("Files")) return;
      e.preventDefault();
      setDropOver(true);
    },
    onDragLeave: (e) => {
      if (!e.currentTarget.contains(e.relatedTarget)) setDropOver(false);
    },
    onDrop: (e) => {
      e.preventDefault();
      setDropOver(false);
      const imgs = imagesFromEvent(e);
      if (imgs.length) onAddImages(item.id, imgs);
    },
  };

  const platform = PLATFORMS.find((p) => p.value === item.platform) || PLATFORMS[0];
  const blocker = item.column === COL.BACKEND ? item.blocker : null;

  return (
    // A fixed panel over the right edge of the page with no backdrop
    // ("persistent" drawer): the board keeps working while it is open, and
    // clicking another card swaps what it shows.
    <Drawer
      variant="persistent"
      anchor="right"
      open
      PaperProps={{
        component: "aside",
        "aria-label": `${itemRef(item)} details`,
        sx: {
          width: { xs: "100%", sm: width },
          bgcolor: A.surface,
          borderLeft: `1px solid ${A.line}`,
          boxShadow: A.shadowLg,
        },
      }}
    >
      {/* Drag the left edge to resize; double-click resets. */}
      <Box
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize panel"
        title="Drag to resize · double-click to reset"
        onMouseDown={startResize}
        onDoubleClick={() => saveWidth(PANEL_DEFAULT_W)}
        sx={{
          display: { xs: "none", sm: "block" },
          position: "absolute",
          top: 0,
          bottom: 0,
          left: -3,
          width: 8,
          zIndex: 2,
          cursor: "col-resize",
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            bottom: 0,
            left: 3,
            width: 2,
            bgcolor: resizing ? A.teal : "transparent",
            transition: "background-color .12s",
          },
          "&:hover::after": { bgcolor: A.tealSoft2 },
        }}
      />
      <Flx column sx={{ height: "100%" }}>
        {/* Header — reference + type; pager through the lane, link, edit /
            delete, close. */}
        <Flx
          ac
          jb
          g={1}
          sx={{ px: 3, py: 1, minHeight: 56, borderBottom: `1px solid ${A.line}`, flexShrink: 0 }}
        >
          <Flx ac g={1.25}>
            <Txt sx={{ fontSize: 13, fontWeight: 600, color: A.muted, letterSpacing: ".04em", fontFamily: A.mono }}>
              {itemRef(item)}
            </Txt>
            <NeedTags needs={item.needs} />
            <AutoPill item={item} />
            <UrgencyPill urgency={item.urgency} />
            {item.sentBack ? (
              <Pill bg={status.warning.bg} fg={status.warning.fg}>
                Sent back
              </Pill>
            ) : null}
          </Flx>
          <Flx ac g={0.25}>
            {lane.length > 1 ? (
              <>
                <Txt sx={{ ...PT.label, mr: 0.5, whiteSpace: "nowrap" }}>
                  {pos} of {lane.length}
                </Txt>
                <HeaderIcon
                  title="Previous in this column"
                  disabled={idx <= 0}
                  onClick={() => onNavigate(lane[idx - 1].id)}
                >
                  <KeyboardArrowUpRounded />
                </HeaderIcon>
                <HeaderIcon
                  title="Next in this column"
                  disabled={idx < 0 || idx >= lane.length - 1}
                  onClick={() => onNavigate(lane[idx + 1].id)}
                >
                  <KeyboardArrowDownRounded />
                </HeaderIcon>
                <Box sx={{ width: "1px", height: 22, bgcolor: A.line, mx: 1 }} />
              </>
            ) : null}
            <HeaderIcon title="Copy link" onClick={copyLink}>
              <LinkRounded />
            </HeaderIcon>
            <HeaderIcon title="View JSON" onClick={() => setJsonOpen(true)}>
              <DataObjectRounded />
            </HeaderIcon>
            <HeaderIcon title="Edit request" onClick={() => onEdit(item)}>
              <EditOutlined />
            </HeaderIcon>
            {deletable ? (
              <HeaderIcon title="Delete request" onClick={() => onDelete(item.id)}>
                <DeleteOutlineRounded />
              </HeaderIcon>
            ) : null}
            <HeaderIcon title="Close" onClick={onClose}>
              <CloseRounded />
            </HeaderIcon>
          </Flx>
        </Flx>

        {/* Body scrolls; header and composer stay put. */}
        <Box
          {...bodyDrop}
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 3,
            pt: 2.5,
            pb: 4,
            outline: dropOver ? `2px dashed ${A.tealSoft2}` : "none",
            outlineOffset: -6,
          }}
        >
          {/* The request and its details, each editable in place: click to
              edit, Enter (title) / Ctrl+Enter (details) or clicking away saves,
              Esc cancels. */}
          <InlineEditText
            key={`title-${item.id}`}
            label="Request"
            value={displayTitle(item.title)}
            required
            onSave={(v) =>
              onUpdate(item.id, "title", isAuto(item.title) ? `${AUTO_PREFIX}${v}` : v, "request")
            }
            textSx={PT.title}
            component="h2"
          />
          <Box sx={{ mt: 0.5 }}>
            <InlineEditText
              key={`desc-${item.id}`}
              label="More details"
              value={item.description || ""}
              multiline
              placeholder="Add more details…"
              onSave={(v) => onUpdate(item.id, "description", v, "more details")}
              textSx={PT.body}
            />
          </Box>
          {/* While it waits on the backend: what's needed, and the tasks. */}
          {blocker ? (
            <Box sx={{ mt: 2.5 }}>
              <BlockerCallout
                blocker={blocker}
                onToggleTask={(i) => onToggleTask(item.id, i)}
                onReadSpec={() =>
                  specRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
                }
                onEdit={() => onEditHandoff(item.id)}
              />
            </Box>
          ) : null}

          {/* The answer to a question (or the way to write one). */}
          <AnswerBlock item={item} onAnswer={onAnswer} onUpdate={onUpdate} />

          {/* Changes the requester made after development had it. */}
          {item.returned || item.requesterEdit ? (
            <Box
              sx={{
                mt: 2.5,
                px: 2,
                py: 1.25,
                borderRadius: "10px",
                bgcolor: status.info.bg,
                color: status.info.fg,
                fontSize: 13,
                lineHeight: 1.55,
              }}
            >
              {item.returned ? (
                <div>
                  <strong>Returned</strong> from {columnByKey(item.returned.from)?.label} by{" "}
                  {nameOf(item.returned.by, me)} · {formatDateTime(item.returned.at, "datetime")}
                </div>
              ) : null}

              {item.requesterEdit ? (
                <div>
                  <strong>Edited</strong> after development started: changed the{" "}
                  {item.requesterEdit.fields.join(", ")} ·{" "}
                  {formatDateTime(item.requesterEdit.at, "datetime")}
                </div>
              ) : null}
            </Box>
          ) : null}

          {/* Properties — one compact label / value list; the stage is the
              first row. */}
          <CollapsibleSection title="Details" sx={{ mt: SECTION }}>
          {/* A bordered table: label column, hairline divider, values. */}
          <Box
            sx={{
              border: `1px solid ${A.line}`,
              borderRadius: "8px",
              overflow: "hidden",
              bgcolor: A.surface,
            }}
          >
            <PropRow label="Stage">
              <ColumnSelect item={item} moves={moves} onMove={onMove} />
            </PropRow>
            <PropRow label="Needs">
              <NeedsPicker
                value={item.needs || []}
                onChange={(needs) => onUpdate(item.id, "needs", needs, "tags")}
              />
            </PropRow>
            <PropRow label="Urgency">
              <InlinePicker
                label="urgency"
                value={item.urgency || "normal"}
                onPick={(v) => onUpdate(item.id, "urgency", v, "urgency")}
                options={URGENCY.map((u) => ({ value: u.key, label: u.label }))}
                display={
                  <span>{URGENCY.find((u) => u.key === (item.urgency || "normal"))?.label}</span>
                }
              />
            </PropRow>
            <PropRow label="Area">
              <Box sx={{ py: "5px" }}>{item.area || "—"}</Box>
            </PropRow>
            <PropRow label="Rank">
              <Flx ac g={0.25}>
                <Box component="span" sx={{ py: "5px", mr: 0.5 }}>
                  {inRequests && isUrgent
                    ? `Urgent · #${sIdx + 1} of ${section.length}`
                    : inRequests
                      ? `#${sIdx + 1} of ${section.length} in the queue`
                      : `#${sIdx + 1} of ${section.length}`}
                </Box>
                {canReorder ? (
                  <>
                    <HeaderIcon
                      title="Move up"
                      small
                      disabled={sIdx <= 0}
                      onClick={() => onReorder(item.id, section[sIdx - 1].id)}
                    >
                      <KeyboardArrowUpRounded />
                    </HeaderIcon>
                    <HeaderIcon
                      title="Move down"
                      small
                      disabled={sIdx < 0 || sIdx >= section.length - 1}
                      onClick={() => onReorder(item.id, section[sIdx + 2]?.id ?? null)}
                    >
                      <KeyboardArrowDownRounded />
                    </HeaderIcon>
                  </>
                ) : null}
              </Flx>
            </PropRow>
            <PropRow label="Requested by">
              <Flx ac g={0.75} sx={{ py: "4px" }}>
                <Avatar person={item.createdBy} size={20} />
                <span>{nameOf(item.createdBy, me)}</span>
              </Flx>
            </PropRow>
            <PropRow label="Company">
              <InlinePicker
                label="company"
                value={platform.value}
                onPick={(v) => onUpdate(item.id, "platform", v, "company")}
                options={PLATFORMS.map((p) => ({
                  value: p.value,
                  label: `${p.short} (${p.label})`,
                }))}
                display={<span>{platform.short}</span>}
              />
            </PropRow>
            <PropRow label="Filed">
              <Box sx={{ py: "5px" }}>{formatDateTime(item.createdDateTime, "datetime")}</Box>
            </PropRow>
          </Box>
          </CollapsibleSection>

          {item.context && item.context.kind !== "text" ? (
            <Box sx={{ mb: 3 }}>
              <FormLinkTargetCard value={item.context} tabsFor={tabsFor} readOnly />
            </Box>
          ) : null}

          {blocker ? (
            <Box>
              <BackendSpecSection
                item={item}
                blocker={blocker}
                sectionRef={specRef}
                onEdit={() => onEditHandoff(item.id)}
                onOpenImages={onOpenImages}
              />
            </Box>
          ) : null}

          {/* Images on the item itself. */}
          <CollapsibleSection
            title={`Images${item.images.length ? ` · ${item.images.length}` : ""}`}
          >
            {/* The dashed tile at the end of the row is the add button; drop and
                paste (anywhere in the panel) also add images. */}
            <ImageThumbs
              images={item.images}
              onOpen={onOpenImages}
              onRemove={(imgId) => onRemoveImage(item.id, imgId)}
              onAdd={() => itemPicker.current?.click()}
              addLabel={`Add images, or paste a screenshot (${MOD_KEY}+V) anywhere in this panel`}
            />
            <input
              ref={itemPicker}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(e) => {
                const imgs = filesToImages(e.target.files);
                if (imgs.length) onAddImages(item.id, imgs);
                e.target.value = "";
              }}
            />
          </CollapsibleSection>

          {/* How often people run into it — above the activity. */}
          <EncountersSection item={item} me={me} onHit={onHit} />

          {/* Activity — history and comments on one rail, oldest first. */}
          <CollapsibleSection
            title="Activity"
            end={
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={activityView}
                  onChange={(_, v) => v && setActivityView(v)}
                  sx={{
                    ...segmentedGroupSx({ fontSize: 12 }),
                    border: `1px solid ${A.line}`,
                    borderRadius: "7px",
                    p: "2px",
                    // Compact: a small inline filter, not a page-level switch.
                    "& .MuiToggleButton-root": {
                      ...segmentedGroupSx({ fontSize: 12 })["& .MuiToggleButton-root"],
                      padding: "2px 8px",
                      lineHeight: 1.5,
                      borderRadius: "5px !important",
                    },
                  }}
                >
                  <ToggleButton value="all">All</ToggleButton>
                  <ToggleButton value="comments">
                    Comments · {item.comments.length}
                  </ToggleButton>
                </ToggleButtonGroup>
              }
          >
            {shown.length ? (
              <Flx column g={2} sx={{ position: "relative" }}>
                {/* The rail behind the icon discs / avatars. */}
                {shown.length > 1 ? (
                  <Box
                    sx={{
                      position: "absolute",
                      left: 13.5,
                      top: 14,
                      bottom: 14,
                      width: "1px",
                      bgcolor: A.line,
                    }}
                  />
                ) : null}
                {shown.map((t) =>
                  t.comment ? (
                    <CommentRow
                      key={t.key}
                      comment={t.comment}
                      me={me}
                      onOpenImages={onOpenImages}
                    />
                  ) : (
                    <EventRow key={t.key} event={t.event} me={me} />
                  ),
                )}
              </Flx>
            ) : (
              <Txt component="div" sx={PT.meta}>
                {activityView === "comments" ? "No comments yet." : "No activity yet."}
              </Txt>
            )}
          </CollapsibleSection>
        </Box>

        {/* Composer — text plus images (paperclip, paste, or drop). */}
        <Box
          ref={composerRef}
          sx={{ px: 3, py: 2, borderTop: `1px solid ${A.line}`, flexShrink: 0 }}
          onDragOver={(e) => {
            if ([...(e.dataTransfer?.types || [])].includes("Files")) e.preventDefault();
          }}
          onDrop={(e) => {
            e.preventDefault();
            const imgs = imagesFromEvent(e);
            if (imgs.length) setDraftImages((prev) => [...prev, ...imgs]);
          }}
        >
          <Flx g={1.5} sx={{ alignItems: "flex-end" }}>
            <Avatar person={me} size={32} />
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                border: `1px solid ${A.lineStrong}`,
                borderRadius: "10px",
                bgcolor: A.surface,
                "&:focus-within": { borderColor: A.teal, boxShadow: `0 0 0 3px ${A.tealSoft}` },
              }}
            >
              {draftImages.length ? (
                <Box sx={{ px: 1.75, pt: 1.5 }}>
                  <ImageThumbs
                    images={draftImages}
                    onOpen={onOpenImages}
                    onRemove={(imgId) =>
                      setDraftImages((prev) => prev.filter((i) => i.id !== imgId))
                    }
                    size={56}
                  />
                </Box>
              ) : null}
              <InputBase
                fullWidth
                multiline
                minRows={1}
                maxRows={6}
                placeholder="Comment, or paste a screenshot…"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onPaste={(e) => {
                  const imgs = imagesFromEvent(e);
                  if (imgs.length) {
                    e.preventDefault();
                    setDraftImages((prev) => [...prev, ...imgs]);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) post();
                }}
                sx={{ px: 1.75, pt: 1.25, fontSize: 12.5, color: A.ink }}
              />
              <Flx ac jb g={1} sx={{ px: 1, pb: 1, pt: 0.5 }}>
                <HeaderIcon title="Attach images" onClick={() => commentPicker.current?.click()}>
                  <AttachFileRounded />
                </HeaderIcon>
                <Flx ac g={1.25}>
                  <Flx ac g={0.5} sx={{ display: { xs: "none", sm: "flex" }, color: A.faint }}>
                    <Kbd>{MOD_KEY}</Kbd>
                    <Kbd>Enter</Kbd>
                    <Txt sx={{ fontSize: 12, color: A.faint, ml: 0.25 }}>to post</Txt>
                  </Flx>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={!canPost}
                    onClick={post}
                    sx={{ height: 32, px: 2, borderRadius: "8px", fontSize: 13 }}
                  >
                    Post
                  </Button>
                </Flx>
              </Flx>
              <input
                ref={commentPicker}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => {
                  const imgs = filesToImages(e.target.files);
                  if (imgs.length) setDraftImages((prev) => [...prev, ...imgs]);
                  e.target.value = "";
                }}
              />
            </Box>
          </Flx>
        </Box>
      </Flx>
      <JsonDialog open={jsonOpen} item={item} onClose={() => setJsonOpen(false)} />
    </Drawer>
  );
}

RequestBoardItemDrawer.propTypes = {
  item: PropTypes.object,
  me: PropTypes.string.isRequired,
  lane: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  onReorder: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onEditHandoff: PropTypes.func.isRequired,
  onToggleTask: PropTypes.func.isRequired,
  onAnswer: PropTypes.func.isRequired,
  onComment: PropTypes.func.isRequired,
  onAddImages: PropTypes.func.isRequired,
  onRemoveImage: PropTypes.func.isRequired,
  onOpenImages: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onHit: PropTypes.func.isRequired,
  history: PropTypes.array,
};
